import { GameMap } from "../map";
import { GameClient, type Board, type Pin, type Note } from "../net";
import { STATIONS } from "../stations";
import { mountQuestions } from "./questions";
import { mountMeasureTab } from "./measureTab";
import { mountSheet } from "./sheet";
import { LAYER_GROUPS, DEFAULT_ON, DIVISION_IDS, makeDivisionPicker, type Tile } from "./seeker";

// Calculator Mode — a standalone seeker toolkit with no game, no chat, no teams.
// Just the map + layers + Ask elimination tools + measure / how-close, on a
// personal board saved to this browser (localStorage). Reachable from the
// dashboard, a no-login ?calc=1 link, or the in-game menu. Fully client-side:
// a LocalCalcClient satisfies the slice of GameClient the reused tools use.

const LS_KEY = "jl_calc_board";
type Kind = "eliminate" | "restore" | "reset" | "cut";
interface CalcAction { id: string; kind: Kind; ids?: string[]; text: string; ts: number; active: boolean }
const cid = () => Math.random().toString(36).slice(2, 10);

class LocalCalcClient {
  identity = { sub: "calc", gm: false, name: "Calculator" } as GameClient["identity"];
  mode: "seekingteams" = "seekingteams";
  seekerKey = "";                       // empty → the "Ask the hider" chat button is hidden
  offLimits: never[] = [];
  zoneOverrides: Record<string, GeoJSON.Geometry> = {};
  zoneOverridesV = 0;
  config = { headStartSec: 2700, bonusPct: 10, zoneMode: "quarter", matchingAssist: true };
  scoresAll = {}; runsAll = {}; threads = {}; locations = new Map();
  hider = null; run = null; pod = null; archives: never[] = [];
  board: Board;
  private actions: CalcAction[] = [];
  private pins: Pin[] = [];
  private notes: Note[] = [];
  private listeners = new Set<() => void>();

  constructor() { this.load(); this.board = this.snapshot(); }

  private load() {
    try { const raw = localStorage.getItem(LS_KEY); if (raw) { const d = JSON.parse(raw); this.actions = d.actions || []; this.pins = d.pins || []; this.notes = d.notes || []; } } catch { /* ignore */ }
  }
  private persist() { try { localStorage.setItem(LS_KEY, JSON.stringify({ actions: this.actions, pins: this.pins, notes: this.notes })); } catch { /* ignore */ } }
  private derive(): Record<string, "eliminated"> {
    const set = new Set<string>();
    for (const a of this.actions) {
      if (!a.active) continue;
      if (a.kind === "reset") set.clear();
      else if (a.kind === "restore") (a.ids || []).forEach((id) => set.delete(id));
      else (a.ids || []).forEach((id) => set.add(id));
    }
    return Object.fromEntries([...set].map((id) => [id, "eliminated" as const]));
  }
  private snapshot(): Board {
    return {
      stations: this.derive(),
      log: this.actions.filter((a) => a.active).map((a) => ({ id: a.id, kind: a.kind, text: a.text, ts: a.ts, count: a.ids?.length ?? 0 })),
      pins: this.pins, notes: this.notes, canUndo: this.actions.some((a) => a.active),
    };
  }
  private emit() { this.board = this.snapshot(); this.persist(); for (const fn of this.listeners) fn(); }
  private push(kind: Kind, ids: string[] | undefined, text: string) { this.actions.push({ id: cid(), kind, ids, text, ts: Date.now(), active: true }); this.emit(); }

  subscribe(fn: () => void) { this.listeners.add(fn); fn(); return () => this.listeners.delete(fn); }
  statusOf(id: string): "active" | "eliminated" { return this.derive()[id] ? "eliminated" : "active"; }
  isOffLimits() { return false; }
  activeCount() { const d = this.derive(); return STATIONS.reduce((n, s) => n + (d[s.id] ? 0 : 1), 0); }
  eliminate(ids: string[], text: string) { this.push("eliminate", ids, text); }
  cut(ids: string[], text: string) { this.push("cut", ids, text); }
  restore(ids: string[], text: string) { this.push("restore", ids, text); }
  reset() { this.push("reset", undefined, "Reset board"); }
  undoLast() { for (let i = this.actions.length - 1; i >= 0; i--) if (this.actions[i].active) { this.actions[i].active = false; break; } this.emit(); }
  undoAction(id: string) { const a = this.actions.find((x) => x.id === id); if (a) a.active = false; this.emit(); }
  toggleStation(id: string) { const elim = !!this.derive()[id]; this.push(elim ? "restore" : "eliminate", [id], elim ? "Restored one station" : "Eliminated one station"); }
  addPin(pin: Pin) { this.pins.push(pin); this.emit(); }
  movePin(id: string, lng: number, lat: number) { const p = this.pins.find((x) => x.id === id); if (p) { p.lng = lng; p.lat = lat; } this.emit(); }
  removePin(id: string) { this.pins = this.pins.filter((x) => x.id !== id); this.emit(); }
  addNote(note: Note) { this.notes.push(note); this.emit(); }
  moveNote(id: string, lng: number, lat: number) { const n = this.notes.find((x) => x.id === id); if (n) { n.lng = lng; n.lat = lat; } this.emit(); }
  editNote(id: string, text: string) { const n = this.notes.find((x) => x.id === id); if (n) n.text = text; this.emit(); }
  removeNote(id: string) { this.notes = this.notes.filter((x) => x.id !== id); this.emit(); }
  postMessage() { /* no chat in calculator mode */ }
}

type Tab = "layers" | "tools";
type Sub = "eliminate" | "measure" | "close";

export function mountCalculator(root: HTMLElement) {
  document.body.classList.add("sk-active");
  const client = new LocalCalcClient() as unknown as GameClient;
  (window as unknown as { calc: GameClient }).calc = client;
  const exit = () => { location.href = location.pathname; }; // drops ?calc=1 → back to route()

  root.innerHTML = `
    <div class="sk">
      <div id="map" class="sk-map"></div>
      <div class="sk-top">
        <button class="nav-btn" id="calc-exit" aria-label="Exit calculator"><span class="calc-back">←</span></button>
        <div class="calc-badge">🧮 CALCULATOR</div>
        <div class="sk-top-r"><div class="sk-fabs"><button class="sk-fab sk-fab-loc" id="sk-locate" aria-label="Locate me">◎</button></div></div>
      </div>
      <div class="sk-toolbadge" id="sk-toolbadge" hidden></div>
      <div class="sk-sheet" id="sk-sheet">
        <div class="sk-grab" id="sk-grab"><span></span><button class="sk-sheet-toggle" id="sk-sheet-toggle" aria-label="Hide panel" title="Hide panel">▾</button></div>
        <div class="sk-brand">
          <span class="sk-dots"><i style="background:#EE352E"></i><i style="background:#FCCC0A"></i><i style="background:#00933C"></i></span>
          <span class="sk-wordmark">CALCULATOR</span>
          <span class="sk-dots"><i style="background:#0039A6"></i><i style="background:#FF6319"></i><i style="background:#B933AD"></i></span>
        </div>
        <div class="sk-tabs" id="sk-tabs">
          <button class="sk-tab" data-tab="layers"><span class="sk-tab-ic">▦</span>Layers</button>
          <button class="sk-tab" data-tab="tools"><span class="sk-tab-ic">🛠</span>Tools</button>
        </div>
        <div class="sk-body jl-scroll" id="sk-body">
          <section data-panel="layers" id="sk-layers"></section>
          <section data-panel="tools" id="sk-tools" hidden>
            <div class="sk-mode-seg" id="sk-subseg">
              <button data-sub="eliminate" class="on">⛔ Eliminate</button>
              <button data-sub="measure">📐 Measure</button>
              <button data-sub="close">📍 How close?</button>
            </div>
            <section id="sk-ask"></section>
            <div id="sk-measure-basic" hidden></div>
            <div id="sk-assist" hidden></div>
            <p class="sk-note" id="sk-measure-note" hidden></p>
          </section>
        </div>
      </div>
    </div>`;

  const gameMap = new GameMap(root.querySelector<HTMLElement>("#map")!);
  gameMap.onStationClick = (id) => { gameMap.select(id); gameMap.flyToStation(id); gameMap.showStationPopup(id); };
  const fitMap = () => gameMap.map.resize();
  window.addEventListener("resize", fitMap);
  window.visualViewport?.addEventListener("resize", fitMap);
  requestAnimationFrame(fitMap);
  setTimeout(fitMap, 350);

  root.querySelector("#calc-exit")!.addEventListener("click", exit);
  root.querySelector("#sk-locate")!.addEventListener("click", () => {
    navigator.geolocation?.getCurrentPosition(
      (p) => gameMap.map.flyTo({ center: [p.coords.longitude, p.coords.latitude], zoom: 15 }),
      () => alert("Couldn't get your location."));
  });

  const sheet = mountSheet(root, fitMap);
  // Active-tool badge (under the CALCULATOR badge): shows the live tool + a quick
  // undo, so context survives when the sheet collapses to a full-screen map.
  const toolBadge = root.querySelector<HTMLElement>("#sk-toolbadge")!;
  let tbLabel: string | null = null, tbN = 0, tbCommit: (() => void) | null = null;
  function renderBadge() {
    toolBadge.hidden = !tbLabel;
    if (!tbLabel) { toolBadge.innerHTML = ""; return; }
    toolBadge.innerHTML = `<span class="tb-name">🛠 ${tbLabel}</span>${tbN > 0 && tbCommit ? `<button class="tb-elim">Eliminate ${tbN}</button>` : ""}<button class="tb-undo" ${client.board.canUndo ? "" : "disabled"}>↶ Undo</button>`;
    toolBadge.querySelector(".tb-elim")?.addEventListener("click", () => tbCommit?.());
    toolBadge.querySelector(".tb-undo")?.addEventListener("click", () => client.undoLast());
  }
  const questions = mountQuestions(root.querySelector<HTMLElement>("#sk-ask")!, client, gameMap, {
    exclude: ["measure"], setFullscreen: (on) => sheet.collapse(on),
    onActive: (l) => { tbLabel = l; if (!l) { tbN = 0; tbCommit = null; } renderBadge(); },
    onPreview: (n, c) => { tbN = n; tbCommit = c; renderBadge(); },
  });
  const measure = mountMeasureTab(root, client, gameMap, "Drag the A &amp; B handles — straight-line distance, nothing eliminated.", (on) => sheet.collapse(on));

  // ---- Layers tab (same grid as the seeker) -----------------------------
  const layerOn = new Map<string, boolean>();
  // Board essentials kept by "Clear overlays" (eliminations live in the board
  // data, not a layer, so they're preserved by any layer toggle automatically).
  const BOARD_KEEP = new Set(["mask", "zones", "dots", "offlimits"]);
  const pickDivision = makeDivisionPicker(gameMap, client);
  function applyTile(t: Tile, on: boolean) {
    layerOn.set(t.id, on);
    if (t.hideElim) gameMap.setHideEliminated(on, client);
    else t.layers?.forEach((id) => gameMap.setLayerVisible(id, on));
    if (DIVISION_IDS.has(t.id)) gameMap.setDivisionPins(t.id, on, pickDivision);
  }
  function clearOverlays() {
    LAYER_GROUPS.flatMap((g) => g.tiles).forEach((t) => { if (!BOARD_KEEP.has(t.id)) applyTile(t, false); });
    renderLayers();
  }
  function resetMapEntirely() {
    if (!confirm("Reset the entire map?\n\nThis clears ALL eliminations and cuts and resets every layer. This cannot be undone.")) return;
    client.reset();
    LAYER_GROUPS.flatMap((g) => g.tiles).forEach((t) => applyTile(t, DEFAULT_ON.has(t.id)));
    renderLayers();
  }
  function renderLayers() {
    const total = [...layerOn.values()].filter(Boolean).length;
    const el = root.querySelector<HTMLElement>("#sk-layers")!;
    el.innerHTML = `
      <div class="sk-clearbar">
        <button class="sk-clearbtn" id="clear-overlays">🧹 Clear overlays<span>keeps zones, dots, off-limits &amp; eliminations</span></button>
        <button class="sk-clearbtn danger" id="reset-map">♻️ Reset map<span>clears eliminations too — asks first</span></button>
      </div>
      <div class="sk-layers-head"><span class="sk-seclabel">MAP LAYERS</span><span class="sk-on-count">${total} on</span></div>
      ${LAYER_GROUPS.map((g) => `
        <div class="sk-group"><div class="sk-group-name">${g.name}</div>
          <div class="sk-tiles">${g.tiles.map((t) => { const on = !!layerOn.get(t.id); return `
            <button class="sk-tile ${on ? "on" : ""}" data-tile="${t.id}" style="--c:${t.color}"><span class="sk-tile-dot">${t.emoji}</span><span class="sk-tile-lbl">${t.label}</span></button>`; }).join("")}</div>
        </div>`).join("")}
      <p class="sk-note">Personal scratchpad — saved on this device. The <b>Tools</b> tab holds Eliminate, Measure &amp; How-close.</p>`;
    el.querySelectorAll<HTMLButtonElement>("[data-tile]").forEach((b) => b.addEventListener("click", () => {
      const t = LAYER_GROUPS.flatMap((g) => g.tiles).find((x) => x.id === b.dataset.tile)!;
      applyTile(t, !layerOn.get(t.id));
      renderLayers();
    }));
    el.querySelector("#clear-overlays")!.addEventListener("click", clearOverlays);
    el.querySelector("#reset-map")!.addEventListener("click", resetMapEntirely);
  }

  // ---- tabs (Eliminate / Measure / How-close are one "Tools" tab) --------
  let tab: Tab = "layers";
  let sub: Sub = "eliminate";
  function setSub(s: Sub) {
    const prev = sub; sub = s;
    root.querySelectorAll<HTMLButtonElement>("#sk-subseg [data-sub]").forEach((b) => b.classList.toggle("on", b.dataset.sub === s));
    root.querySelector<HTMLElement>("#sk-ask")!.hidden = s !== "eliminate";
    if (prev === "eliminate" && s !== "eliminate") questions.deactivate();
    if (s === "eliminate") {
      measure.leave();
      root.querySelector<HTMLElement>("#sk-measure-basic")!.hidden = true;
      root.querySelector<HTMLElement>("#sk-assist")!.hidden = true;
      root.querySelector<HTMLElement>("#sk-measure-note")!.hidden = true;
    } else {
      measure.enter(s);
    }
  }
  function setTab(t: Tab) {
    const prev = tab; tab = t;
    root.querySelectorAll<HTMLElement>("[data-panel]").forEach((p) => { p.hidden = p.dataset.panel !== t; });
    root.querySelectorAll<HTMLButtonElement>(".sk-tab").forEach((b) => b.classList.toggle("on", b.dataset.tab === t));
    if (prev === "tools" && t !== "tools") { questions.deactivate(); measure.leave(); }
    if (sheet.name === "peek") sheet.set("half");
    if (t === "tools") setSub(sub);
  }
  root.querySelectorAll<HTMLButtonElement>(".sk-tab").forEach((b) => b.addEventListener("click", () => setTab(b.dataset.tab as Tab)));
  root.querySelectorAll<HTMLButtonElement>("#sk-subseg [data-sub]").forEach((b) => b.addEventListener("click", () => setSub(b.dataset.sub as Sub)));

  client.subscribe(() => {
    gameMap.refresh(client);
    gameMap.syncPins(client.board.pins, (id, lng, lat) => client.movePin(id, lng, lat), (id) => client.removePin(id));
    const tbUndo = toolBadge.querySelector<HTMLButtonElement>(".tb-undo");
    if (tbUndo) tbUndo.disabled = !client.board.canUndo;
  });

  renderLayers();
  LAYER_GROUPS.flatMap((g) => g.tiles).forEach((t) => applyTile(t, DEFAULT_ON.has(t.id)));
  renderLayers();
  setTab("layers");
}
